import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { trackEvent } from "../lib/analytics";
import { Footer, Nav } from "../components/lidoo/chrome";
import { catalogSize, createPartnerApplication } from "../lib/api/lidoo.functions";
import { useI18n } from "../lib/i18n";
import { hreflangLinks } from "../lib/seo";

export const Route = createFileRoute("/partner")({
  loader: async () => await catalogSize(),
  head: () => ({
    meta: [
      { title: "List your beach club, free | sunbedly" },
      {
        name: "description",
        content:
          "A free page for your beach club in six languages, with your own prices, your own dashboard and guests who contact you directly. No commission, no contract.",
      },
    ],
    links: hreflangLinks("/partner"),
  }),
  component: () => <PartnerPage stats={Route.useLoaderData()} />,
});

export type CatalogStats = { clubs: number; countries: number; regions: number };

export function PartnerPage({ stats }: { stats: CatalogStats }) {
  const { t, lang } = useI18n();
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [form, setForm] = useState({
    clubName: "",
    contactName: "",
    contact: "",
    country: "",
    town: "",
    instagram: "",
    hasBookingSystem: "unknown" as "yes" | "no" | "unknown",
    message: "",
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState("sending");
    try {
      const res = await createPartnerApplication({ data: { ...form, locale: lang } });
      if (res.ok) trackEvent("partner_apply", { club_name: form.clubName, country: form.country });
      setState(res.ok ? "done" : "error");
    } catch {
      setState("error");
    }
  };

  const field =
    "li-display h-11 w-full rounded-lg border border-[#1C2430]/15 bg-white/70 px-3 text-sm text-[#1C2430] placeholder:text-[#5B6472] focus:border-[#2C4FD6] focus:outline-none";
  const label = "li-mono mb-1 block text-[11px] uppercase tracking-wider text-[#5B6472]";

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <Nav />
      <main className="pb-20">
        {/* Hero. The three numbers underneath are read straight from the
            database, so this page can never claim a catalog we do not have. */}
        <section className="mx-auto max-w-6xl px-5 pt-14">
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#2C4FD6]">
            {t("b2b_eyebrow")}
          </p>
          <h1 className="li-serif mt-4 max-w-[20ch] text-4xl leading-[1.02] text-[#1C2430] md:text-7xl">
            {t("b2b_title")}
          </h1>
          <p className="mt-6 max-w-[56ch] text-lg leading-relaxed text-[#5B6472]">{t("b2b_sub")}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#apply"
              onClick={() => trackEvent("b2b_cta_click", { position: "hero" })}
              className="li-display inline-flex h-12 items-center rounded-full border-2 border-[#1C2430] bg-[#1C2430] px-7 text-[15px] font-bold text-[#F2EFE9] transition-colors hover:border-[#2C4FD6] hover:bg-[#2C4FD6]"
            >
              {t("b2b_cta")}
            </a>
            <span className="li-mono max-w-[30ch] text-[12px] text-[#5B6472]">
              {t("b2b_cta_sub")}
            </span>
          </div>

          <dl className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-[#1C2430]/10 bg-[#1C2430]/10 sm:grid-cols-3">
            {[
              { v: String(stats.clubs), k: "b2b_stat_clubs" },
              { v: "6", k: "b2b_stat_langs" },
              { v: "0%", k: "b2b_stat_fee" },
            ].map((s) => (
              <div key={s.k} className="bg-[#F2EFE9] px-6 py-7">
                <dt className="li-serif text-4xl text-[#1C2430] md:text-5xl">{s.v}</dt>
                <dd className="li-mono mt-1 text-[11px] uppercase tracking-widest text-[#5B6472]">
                  {t(s.k)}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* What you get */}
        <section className="mx-auto mt-20 max-w-6xl px-5">
          <h2 className="li-serif text-3xl text-[#1C2430] md:text-4xl">{t("b2b_what_title")}</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              ["b2b_w1_t", "b2b_w1_d"],
              ["b2b_w2_t", "b2b_w2_d"],
              ["b2b_w3_t", "b2b_w3_d"],
              ["b2b_w4_t", "b2b_w4_d"],
            ].map(([tk, dk]) => (
              <div key={tk} className="rounded-2xl border border-[#1C2430]/10 bg-white/70 p-6">
                <h3 className="li-serif text-xl text-[#1C2430]">{t(tk)}</h3>
                <p className="mt-2 max-w-[46ch] text-sm leading-relaxed text-[#5B6472]">{t(dk)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* The comparison. Every claim in the right-hand column is about the
            common model in this market, not about a named competitor. */}
        <section className="mt-20 bg-[#E7E2D6] py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="li-serif text-3xl text-[#1C2430] md:text-4xl">{t("b2b_diff_title")}</h2>
            <div className="mt-8 overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-[#1C2430]/20">
                    <th className="py-3 pr-4" />
                    <th className="li-mono py-3 pr-4 text-[11px] uppercase tracking-widest text-[#2C4FD6]">
                      {t("b2b_diff_us")}
                    </th>
                    <th className="li-mono py-3 text-[11px] uppercase tracking-widest text-[#5B6472]">
                      {t("b2b_diff_them")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <tr key={n} className="border-b border-[#1C2430]/10">
                      <td className="py-4 pr-4 text-sm text-[#1C2430]">{t(`b2b_d${n}`)}</td>
                      <td className="li-display py-4 pr-4 text-sm font-bold text-[#1C2430]">
                        {t(`b2b_d${n}_us`)}
                      </td>
                      <td className="py-4 text-sm text-[#5B6472]">{t(`b2b_d${n}_them`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Saying out loud that we are small is what makes the rest of the
                page believable to an owner who has been oversold before. */}
            <div className="mt-10 max-w-[62ch] border-l-2 border-[#2C4FD6] pl-5">
              <h3 className="li-serif text-xl text-[#1C2430]">{t("b2b_honest_title")}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">
                {t("b2b_honest_d", { clubs: String(stats.clubs) })}
              </p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="mx-auto mt-20 max-w-6xl px-5">
          <h2 className="li-serif text-3xl text-[#1C2430] md:text-4xl">{t("b2b_how_title")}</h2>
          <ol className="mt-8 grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <li key={n} className="border-t-2 border-[#2C4FD6] pt-4">
                <span className="li-mono text-[12px] text-[#2C4FD6]">0{n}</span>
                <h3 className="li-serif mt-2 text-xl text-[#1C2430]">{t(`b2b_how_${n}_t`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">{t(`b2b_how_${n}_d`)}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* FAQ, with FAQPage markup so these answers can surface in search and
            in AI assistants that owners increasingly ask first. */}
        <section className="mx-auto mt-20 max-w-6xl px-5">
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: [1, 2, 3, 4, 5].map((n) => ({
                  "@type": "Question",
                  name: t(`b2b_fq${n}`),
                  acceptedAnswer: { "@type": "Answer", text: t(`b2b_fa${n}`) },
                })),
              }),
            }}
          />
          <h2 className="li-serif text-3xl text-[#1C2430] md:text-4xl">{t("b2b_faq_title")}</h2>
          <dl className="mt-8 divide-y divide-[#1C2430]/10 border-y border-[#1C2430]/10">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="py-5">
                <dt className="li-display text-base font-bold tracking-tight text-[#1C2430]">
                  {t(`b2b_fq${n}`)}
                </dt>
                <dd className="mt-2 max-w-[68ch] text-sm leading-relaxed text-[#5B6472]">
                  {t(`b2b_fa${n}`)}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <div id="apply" className="mx-auto mt-20 grid max-w-6xl gap-12 px-5 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <h2 className="li-serif max-w-[16ch] text-3xl leading-none text-[#1C2430] md:text-5xl">
              {t("partner_title")}
            </h2>
            <p className="mt-5 max-w-[52ch] text-base leading-relaxed text-[#5B6472]">
              {t("partner_sub")}
            </p>
            <div className="mt-10">
              {[
                { n: "01", tKey: "partner_p1_t", dKey: "partner_p1_d" },
                { n: "02", tKey: "partner_p2_t", dKey: "partner_p2_d" },
                { n: "03", tKey: "partner_p3_t", dKey: "partner_p3_d" },
              ].map((p) => (
                <div key={p.n} className="border-t border-[#1C2430]/15 py-5">
                  <div className="flex items-baseline gap-4">
                    <span className="li-mono text-sm text-[#2C4FD6]">{p.n}</span>
                    <div>
                      <h3 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
                        {t(p.tKey)}
                      </h3>
                      <p className="mt-1 max-w-[50ch] text-sm leading-relaxed text-[#5B6472]">
                        {t(p.dKey)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 hidden overflow-hidden rounded-xl lg:block">
              <img
                src="/assets/lido-side-800.webp"
                alt="Beach club jetty and parasols"
                loading="lazy"
                className="h-[260px] w-full object-cover"
              />
            </div>
          </div>

          <div>
            {state === "done" ? (
              <div className="rounded-xl border border-[#2C4FD6]/40 bg-[#2C4FD6]/5 p-8">
                <p className="li-display text-xl font-bold text-[#1C2430]">{t("partner_ok_t")}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">{t("partner_ok_d")}</p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="rounded-xl border border-[#1C2430]/10 bg-white/50 p-6"
              >
                <div className="grid gap-4">
                  <div>
                    <label htmlFor="pt-club" className={label}>
                      {t("partner_form_club")}
                    </label>
                    <input
                      id="pt-club"
                      required
                      value={form.clubName}
                      onChange={(e) => setForm({ ...form, clubName: e.target.value })}
                      className={field}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="pt-name" className={label}>
                        {t("partner_form_name")}
                      </label>
                      <input
                        id="pt-name"
                        required
                        value={form.contactName}
                        onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                        className={field}
                      />
                    </div>
                    <div>
                      <label htmlFor="pt-contact" className={label}>
                        {t("partner_form_contact")}
                      </label>
                      <input
                        id="pt-contact"
                        required
                        value={form.contact}
                        onChange={(e) => setForm({ ...form, contact: e.target.value })}
                        className={field}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="pt-country" className={label}>
                        {t("partner_form_country")}
                      </label>
                      <input
                        id="pt-country"
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                        className={field}
                      />
                    </div>
                    <div>
                      <label htmlFor="pt-town" className={label}>
                        {t("partner_form_town")}
                      </label>
                      <input
                        id="pt-town"
                        value={form.town}
                        onChange={(e) => setForm({ ...form, town: e.target.value })}
                        className={field}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="pt-ig" className={label}>
                      {t("partner_form_instagram")}
                    </label>
                    <input
                      id="pt-ig"
                      value={form.instagram}
                      onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                      className={field}
                      placeholder="@"
                    />
                  </div>
                  <div>
                    <span className={label}>{t("partner_form_system")}</span>
                    <div className="flex gap-2">
                      {(
                        [
                          ["yes", "partner_sys_yes"],
                          ["no", "partner_sys_no"],
                          ["unknown", "partner_sys_unknown"],
                        ] as const
                      ).map(([value, key]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setForm({ ...form, hasBookingSystem: value })}
                          className={`li-mono rounded-md border px-3 py-2 text-[11px] transition-colors ${
                            form.hasBookingSystem === value
                              ? "border-[#2C4FD6] bg-[#2C4FD6] text-[#F2EFE9]"
                              : "border-[#1C2430]/20 text-[#1C2430] hover:border-[#2C4FD6]"
                          }`}
                        >
                          {t(key)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="pt-msg" className={label}>
                      {t("partner_form_message")}
                    </label>
                    <textarea
                      id="pt-msg"
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="li-display w-full rounded-lg border border-[#1C2430]/15 bg-white/70 px-3 py-2 text-sm text-[#1C2430] focus:border-[#2C4FD6] focus:outline-none"
                    />
                  </div>
                  {state === "error" ? (
                    <p className="text-sm text-[#B03030]">{t("request_error")}</p>
                  ) : null}
                  <button
                    type="submit"
                    disabled={state === "sending"}
                    className="li-display h-12 rounded-lg border-2 border-[#1C2430] bg-[#1C2430] px-5 text-[15px] font-bold text-[#F2EFE9] transition-colors hover:border-[#2C4FD6] hover:bg-[#2C4FD6] active:translate-y-[1px] disabled:opacity-60"
                  >
                    {state === "sending" ? t("form_sending") : t("partner_send")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
